import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

// Registry-backed vertical record (Task #208). Replaces the four historical
// hardcoded frontend option lists (InitiativeSetup / Verticals / MeasureHub /
// BulkUpload) plus the CEDAR/LEAF document-scope vertical tokens with a single
// managed source.
export interface VerticalRecord {
  id: number;
  uuid: string;
  key: string;
  label: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  status: string;
  is_visible: boolean;
  sort_order: number;
  default_governance?: Record<string, unknown> | null;
  default_scope?: Record<string, unknown> | null;
}

// VERT-01 helpers — read the governed taxonomy metadata off a registry row.
// The alias mechanism itself is BACKEND-owned (archived alias rows carry
// `default_scope.alias_of` / `alias_condition_focus`); these are display-only
// accessors, never a frontend mapping.
export function conditionTags(v: VerticalRecord): string[] {
  const tags = (v.default_scope as Record<string, unknown> | null | undefined)?.condition_tags;
  return Array.isArray(tags) ? tags.filter((t): t is string => typeof t === 'string') : [];
}

export function aliasOf(v: VerticalRecord): string | null {
  const target = (v.default_scope as Record<string, unknown> | null | undefined)?.alias_of;
  return typeof target === 'string' && target ? target : null;
}

export function aliasConditionFocus(v: VerticalRecord): string | null {
  const focus = (v.default_scope as Record<string, unknown> | null | undefined)?.alias_condition_focus;
  return typeof focus === 'string' && focus ? focus : null;
}

/** Active but hidden from public surfaces — a Planned/Hidden vertical. */
export function isPlannedHidden(v: VerticalRecord): boolean {
  return v.status === 'active' && !v.is_visible;
}

interface UseVerticalsResult {
  verticals: VerticalRecord[];
  loading: boolean;
  error: string | null;
  viewerIsStaff: boolean;
  reload: () => Promise<void>;
}

// Fetches the vertical registry. Visibility gating is enforced SERVER-SIDE from
// the caller's credentials: non-staff (public/preview) callers only ever receive
// visible + active verticals; staff receive the full set. `adminFetch` carries
// the staff session cookie and optional break-glass admin code, so the same hook
// is safe on both public and internal surfaces.
export function useVerticals(includeArchived = false): UseVerticalsResult {
  const [verticals, setVerticals] = useState<VerticalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerIsStaff, setViewerIsStaff] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const qs = includeArchived ? '?include_archived=true' : '';
      const res = await apiFetch(`/api/verticals${qs}`, { context: 'admin' });
      if (!res.ok) throw new Error(`Failed to load verticals (${res.status})`);
      const data = await res.json();
      setVerticals(Array.isArray(data.items) ? data.items : []);
      setViewerIsStaff(Boolean(data.viewer_is_staff));
      setError(null);
    } catch (e) {
      setVerticals([]);
      setError('Could not load verticals from backend.');
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { verticals, loading, error, viewerIsStaff, reload };
}
